import { api } from "./api";
import { setGeo, userStore } from "../redux/user";
import { v4 } from "uuid";

import {
  doStartReconnectTimer,
  endReconnectTimer,
  setOtherLoginShow,
  setWsConnected,
  updateReconnectTimer,
  wsConnectionStore,
  setWebsocketUrl,
} from "../redux/wsConnection";

import { routeIncommingMessage } from "./wsInRoutes";
import { LoginWs } from "./wsOutRoutes";
import { wsDebug } from "./wsDebug";
import {
  reportRussiaSiteSettings,
  reportRussiaSiteSettingsFailed,
  reportRussiaWebsocketEvent,
} from "./clientHealth";

let offlineDetectionInitialized = false;
let offlineTimeOut;

export let websocket = null;
let pingInterval: any;
let postTimeout: any;
export let longPollingUrl = null;
let useLongPolling = false;
let longPollingSwitchStarted = false;

const PING_INTERVAL_MS = 5000;
const CLIENT_RECONNECT_CLOSE_CODE = 4000;
const AUTO_LONG_POLLING_FALLBACK_ENABLED =
  process.env.NEXT_PUBLIC_RU_AUTO_LONG_POLLING !== "false";
const AUTO_LONG_POLLING_CLOSE_WINDOW_MS = 10 * 60 * 1000;
const AUTO_LONG_POLLING_MIN_1006_CLOSES = 2;
const AUTO_LONG_POLLING_GAME_PATH_RE = /^\/(?:ru\/)?game(?:\/|$)/;

let lastPingAt = 0;
let lastPongAt = 0;
let lastMessageAt = 0;
let websocketOpenedAt = 0;
let websocketUserKey = "";
let watchdogLiveGameActive = false;
let autoLongPollingFallbackStarted = false;
let liveGame1006CloseGameId = "";
let liveGame1006CloseCount = 0;
let firstLiveGame1006CloseAt = 0;

export function setWebsocketWatchdogLiveGameActive(active: boolean) {
  watchdogLiveGameActive = active;
}

export function isLongPollingActive() {
  return useLongPolling;
}

function shouldUseLongPollingTransport() {
  if (!process.browser) {
    return false;
  }

  const params = new URLSearchParams(window.location.search || "");
  return (
    params.get("transport") === "polling" || params.get("forcePolling") === "1"
  );
}

function isRussiaLikelyClient() {
  if (!process.browser) {
    return false;
  }

  const { geo, country } = userStore.getState();
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname || "";
  const language = navigator.language || "";
  const languages = Array.from(navigator.languages || []);

  return (
    String(geo || "").toUpperCase() === "RU" ||
    String(country || "").toUpperCase() === "RU" ||
    host === "ru.chesshotel.com" ||
    host === "test-ru.chesshotel.com" ||
    /^\/ru(?:\/|$)/.test(path) ||
    /^ru\b/i.test(language) ||
    languages.some((item) => /^ru\b/i.test(item || "")) ||
    window.__chesshotelRussiaClientHealth?.enabled === true
  );
}

function isAutoLongPollingFallbackDisabled() {
  if (!process.browser) {
    return true;
  }

  const params = new URLSearchParams(window.location.search || "");
  if (params.get("disablePollingFallback") === "1") {
    return true;
  }

  try {
    return localStorage.getItem("chesshotel:disable-polling-fallback") === "1";
  } catch (_) {
    return false;
  }
}

function hasAutoLongPollingLiveGameContext(payload: Record<string, any> = {}) {
  if (watchdogLiveGameActive || payload.gameId) {
    return true;
  }

  if (!process.browser) {
    return false;
  }

  return AUTO_LONG_POLLING_GAME_PATH_RE.test(window.location.pathname || "");
}

function getAutoLongPollingEligibility(payload: Record<string, any> = {}) {
  const { longPollingUrl } = wsConnectionStore.getState();
  const { username } = userStore.getState();

  return {
    enabled: AUTO_LONG_POLLING_FALLBACK_ENABLED,
    disabled: isAutoLongPollingFallbackDisabled(),
    russiaLikely: isRussiaLikelyClient(),
    online: process.browser ? navigator.onLine : false,
    visible: process.browser ? document.visibilityState === "visible" : false,
    hasLongPollingUrl: !!longPollingUrl,
    hasUsername: !!username,
    liveGameContext: hasAutoLongPollingLiveGameContext(payload),
    alreadyStarted: autoLongPollingFallbackStarted || useLongPolling,
  };
}

function canStartAutoLongPollingFallback(payload: Record<string, any> = {}) {
  const eligibility = getAutoLongPollingEligibility(payload);
  return (
    eligibility.enabled &&
    !eligibility.disabled &&
    eligibility.russiaLikely &&
    eligibility.online &&
    eligibility.visible &&
    eligibility.hasLongPollingUrl &&
    eligibility.hasUsername &&
    eligibility.liveGameContext &&
    !eligibility.alreadyStarted
  );
}

async function startLongPollingTransport(
  reason: string,
  payload: Record<string, any> = {}
) {
  if (useLongPolling || longPollingSwitchStarted) {
    return;
  }

  longPollingSwitchStarted = true;
  useLongPolling = true;
  clearInterval(pingInterval);

  const { username } = userStore.getState();
  const sessionId = getOrCreateSessionId();
  const { longPollingUrl } = wsConnectionStore.getState();
  const { gamePlayStore } = await import("../redux/gamePlay");
  const { gameId } = gamePlayStore.getState();
  const { longPollingGetRequest } = await import("./longPolling");

  reportRussiaWebsocketEvent("long_polling_enabled", {
    reason,
    ...payload,
    url: longPollingUrl,
    websocketUrl: websocket?.url || null,
    gameId: gameId || null,
  });

  setWsConnected(true);
  endReconnectTimer();
  setupOfflineDetection();
  longPollingGetRequest(username, sessionId, gameId || "");
}

function startAutoLongPollingFallback(
  reason: string,
  payload: Record<string, any> = {}
) {
  const eligibility = getAutoLongPollingEligibility(payload);

  if (!canStartAutoLongPollingFallback(payload)) {
    reportRussiaWebsocketEvent("long_polling_auto_fallback_skipped", {
      reason,
      eligibility,
      ...payload,
    });
    return false;
  }

  autoLongPollingFallbackStarted = true;
  void startLongPollingTransport(reason, {
    automatic: true,
    eligibility,
    ...payload,
  }).catch((error) => {
    autoLongPollingFallbackStarted = false;
    useLongPolling = false;
    longPollingSwitchStarted = false;
    reportRussiaWebsocketEvent("long_polling_start_failed", {
      reason,
      message: error?.message,
    });
    initWebsocketConnect();
  });

  return true;
}

export function stopLongPollingTransport(
  reason: string,
  payload: Record<string, any> = {}
) {
  if (!useLongPolling && !longPollingSwitchStarted) {
    return;
  }

  useLongPolling = false;
  longPollingSwitchStarted = false;
  autoLongPollingFallbackStarted = false;

  reportRussiaWebsocketEvent("long_polling_stopped", {
    reason,
    ...payload,
  });

  setWsConnected(false);
  disconnectWebSocket(`stop long polling: ${reason}`);
  initWebsocketConnect();
}

function getWebsocketUserKey() {
  const { username, isGuest, id, room } = userStore.getState();
  return [username || "", isGuest ? "guest" : "member", id || "", room || ""]
    .map((value) => String(value))
    .join(":");
}

function getBrowserConnectionInfo() {
  if (!process.browser) {
    return {};
  }

  const nav = navigator as any;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection || {};

  return {
    networkState: navigator.onLine ? "online" : "offline",
    visibilityState: document.visibilityState,
    effectiveType: connection.effectiveType || null,
    downlink: connection.downlink || null,
    rtt: connection.rtt || null,
    saveData: connection.saveData || false,
  };
}

function setPingErrorData(update: {
  eventSequence?: Record<string, any>;
  websocketWatchdog?: Record<string, any>;
  [key: string]: any;
}) {
  if (process.env.NODE_ENV === "development") {
    return;
  }

  import("../redux/ping").then((module) => {
    const state = module.pingStore.getState();
    module.setErrorData({
      ...update,
      ...(update.eventSequence && {
        eventSequence: {
          ...state.errorData.eventSequence,
          ...update.eventSequence,
        },
      }),
      ...(update.websocketWatchdog && {
        websocketWatchdog: {
          ...state.errorData.websocketWatchdog,
          ...update.websocketWatchdog,
        },
      }),
    });
  });
}

function getWatchdogState(reason: string, now = Date.now()) {
  return {
    reason,
    lastCheckAt: now,
    lastPingAt: lastPingAt || null,
    lastPongAt: lastPongAt || null,
    lastMessageAt: lastMessageAt || null,
    websocketOpenedAt: websocketOpenedAt || null,
    stalePongMs: lastPongAt ? now - lastPongAt : null,
    staleMessageMs: lastMessageAt ? now - lastMessageAt : null,
    openedForMs: websocketOpenedAt ? now - websocketOpenedAt : null,
    readyState: websocket?.readyState ?? null,
    url: websocket?.url || null,
    liveGameActive: watchdogLiveGameActive,
    reconnectWatchdogEnabled: false,
    ...getBrowserConnectionInfo(),
  };
}

function recordWatchdogState(reason: string, now = Date.now()) {
  setPingErrorData({
    websocketWatchdog: getWatchdogState(reason, now),
  });
}

function getCompactWatchdogState(state?: Record<string, any> | null) {
  if (!state) {
    return null;
  }

  return {
    reason: state.reason,
    lastCheckAt: state.lastCheckAt,
    stalePongMs: state.stalePongMs,
    staleMessageMs: state.staleMessageMs,
    openedForMs: state.openedForMs,
    readyState: state.readyState,
    reconnectWatchdogEnabled: state.reconnectWatchdogEnabled,
    networkState: state.networkState,
    visibilityState: state.visibilityState,
    effectiveType: state.effectiveType,
    rtt: state.rtt,
  };
}

function setupOfflineDetection() {
  if (offlineDetectionInitialized) return;
  offlineDetectionInitialized = true;

  window.addEventListener("offline", (event) => {
    import("../redux/ping").then((module) => {
      module.setOffline();
      module.setErrorData({
        networkState: "offline",
        networkStateTime: Date.now(),
      });

      offlineTimeOut = setTimeout(() => {
        startReconnectTimer();
      }, 3000);
    });
  });

  window.addEventListener("online", (event) => {
    import("../redux/ping").then((module) => {
      module.setErrorData({
        networkState: "online",
        networkStateTime: Date.now(),
      });
      module.resetRetries();
      clearTimeout(offlineTimeOut);
      endReconnectTimer();
      initWebsocketConnect();
    });
  });
}

export function getOrCreateSessionId() {
  let sessionId: string = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = v4();
    sessionStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

async function loginServer() {
  const { username, isGuest, id, room } = userStore.getState();
  import("../redux/gamePlay").then(({ gamePlayStore }) => {
    const { gameId } = gamePlayStore.getState();

    try {
      const sessionId = getOrCreateSessionId();

      const user = {
        username,
        isGuest,
        userId: id,
        room,
        gameId,
        sessionId,
      };

      LoginWs.loginServer(user);
      reportRussiaWebsocketEvent("websocket_login_sent", {
        isGuest,
        userId: id || null,
        room,
        gameId: gameId || null,
      });
    } catch (e) {
      setTimeout(() => {
        loginServer();
      }, 3000);
    }
  });
}

function startReconnectTimer() {
  const reconnectStart = wsConnectionStore.getState().reconnectStart;
  if (!reconnectStart) {
    doStartReconnectTimer();

    const loop = () => {
      const startTime = wsConnectionStore.getState().reconnectStart;
      if (startTime == null) {
        return;
      }
      updateReconnectTimer();
      requestAnimationFrame(loop);
    };

    loop();
  }
}

export function initWebsocketConnect() {
  if (!shouldUseLongPollingTransport() && !autoLongPollingFallbackStarted) {
    useLongPolling = false;
    longPollingSwitchStarted = false;
  }

  if (useLongPolling) {
    return;
  }

  wsDebug("initWebsocketConnect: fetching site settings", {
    baseURL: api.defaults.baseURL,
  });

  api
    .get(`/site/settings?_=${new Date().getTime()}`)
    .then(async (response) => {
      const { ws, geo } = response.data;
      wsDebug("site/settings response", {
        ws,
        geo,
        host: process.env.NEXT_PUBLIC_HOST,
      });
      reportRussiaSiteSettings({ geo, ws });

      setWebsocketUrl(ws);
      connectToWebSocket();
      const localGeo = localStorage.getItem("geo");
      if (localGeo) {
        setGeo(localGeo);
      } else {
        setGeo(geo);
      }
    })
    .catch((error) => {
      console.log("get ws number error", error);
      wsDebug("site/settings request failed", {
        message: error?.message,
        status: error?.response?.status,
      });
      reportRussiaSiteSettingsFailed(error);
      setTimeout(() => {
        initWebsocketConnect();
      }, 1000);
    });
}

//Happens when server code update happens and it switches to a new server instance
export async function checkWs(ws: number) {
  import("../redux/gamePlay").then(({ gamePlayStore }) => {
    const { gameEnded } = gamePlayStore.getState();

    const isPlaying = !gameEnded;
    if (isPlaying) {
      const unsubscribe = gamePlayStore.subscribe(({ gameEnded }) => {
        if (gameEnded && wsConnectionStore.getState().ws !== ws) {
          setTimeout(() => {
            unsubscribe();
            setWebsocketUrl(ws);
            connectToWebSocket();
          }, 2000);
        }
      });
    }

    if (!isPlaying && wsConnectionStore.getState().ws !== ws) {
      setWebsocketUrl(ws);
      connectToWebSocket();
    }
  });
}

export function otherUserLogin() {
  disconnectWebSocket("other login");
  setOtherLoginShow(true);
}

export function disconnectWebSocket(reason = "client reconnect") {
  clearInterval(pingInterval);

  if (websocket) {
    websocket.onclose = function () {};
    try {
      websocket.close(CLIENT_RECONNECT_CLOSE_CODE, reason);
    } catch (e) {
      websocket.close();
    }
  }
}

let retries = 0;
const LIVE_CLOSE_REPORT_DEDUPE_MS = 30000;
let lastLiveCloseReportKey = "";
let lastLiveCloseReportAt = 0;

export function incrRetries() {
  retries++;
}

function shouldReportLiveClose(
  gameId: string | null,
  code: number,
  now: number,
) {
  const key = `${gameId || "no-game"}:${code}`;
  if (
    lastLiveCloseReportKey === key &&
    now - lastLiveCloseReportAt < LIVE_CLOSE_REPORT_DEDUPE_MS
  ) {
    return false;
  }

  lastLiveCloseReportKey = key;
  lastLiveCloseReportAt = now;
  return true;
}

function noteLiveGame1006Close(gameId: string, now: number) {
  if (
    liveGame1006CloseGameId !== gameId ||
    !firstLiveGame1006CloseAt ||
    now - firstLiveGame1006CloseAt > AUTO_LONG_POLLING_CLOSE_WINDOW_MS
  ) {
    liveGame1006CloseGameId = gameId;
    liveGame1006CloseCount = 0;
    firstLiveGame1006CloseAt = now;
  }

  liveGame1006CloseCount += 1;
  return liveGame1006CloseCount;
}

function connectToWebSocket() {
  const url = wsConnectionStore.getState().websocketUrl;
  const userKey = getWebsocketUserKey();

  if (
    websocket &&
    websocket.url === url &&
    websocketUserKey === userKey &&
    (websocket.readyState === WebSocket.OPEN ||
      websocket.readyState === WebSocket.CONNECTING)
  ) {
    wsDebug("connectToWebSocket: reusing existing socket", {
      url,
      readyState: websocket.readyState,
    });
    return;
  }

  disconnectWebSocket("client reconnect");

  setWsConnected(false);
  websocketOpenedAt = 0;
  lastPingAt = 0;
  lastPongAt = 0;
  lastMessageAt = 0;
  websocketUserKey = userKey;

  wsDebug("connectToWebSocket", { url });
  websocket = new window.WebSocket(url);

  websocket.onerror = (error: Event) => {
    wsDebug("websocket error", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_error", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });

    import("../redux/ping").then((module) => {
      module.setWsError();
      module.setErrorData({
        lastWebsocketError: {
          readyState: websocket?.readyState,
          url: websocket?.url,
          timestamp: Date.now(),
        },
      });
    });
  };

  websocket.onclose = (e: CloseEvent) => {
    if (useLongPolling) {
      reportRussiaWebsocketEvent("websocket_close_ignored_for_polling", {
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
        url: websocket?.url,
      });
      return;
    }

    console.log("websocket close");
    wsDebug("websocket close", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_close", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      readyState: websocket?.readyState,
      url: websocket?.url,
      retries,
    });

    import("../redux/ping").then((module) => {
      import("../redux/gamePlay").then(({ gamePlayStore }) => {
        const now = Date.now();
        const pingState = module.pingStore.getState();
        const closeReadyState = websocket?.readyState ?? null;
        const closeUrl = websocket?.url || null;
        module.setWsClose();
        module.setWsCloseErrorMsg(e.code + " " + e.reason);
        module.setDisconnectTime();
        module.setErrorData({
          eventSequence: {
            ...pingState.errorData.eventSequence,
            lastWsClose: now,
            timeSinceLastVisibilityChange:
              now -
              (pingState.errorData.connectionState.lastVisibilityChange || 0),
            timeSinceLastPing:
              now - (pingState.errorData.eventSequence.lastPingSent || 0),
            timeSinceLastPong:
              now - (pingState.errorData.eventSequence.lastPongReceived || 0),
          },
          networkStateOnClose:
            process.browser && navigator.onLine ? "online" : "offline",
          networkStateTimeOnClose: now,
          websocketWatchdog: {
            ...pingState.errorData.websocketWatchdog,
            ...getWatchdogState("websocket-close", now),
          },
        });

        const { gameEnded, gameId } = gamePlayStore.getState();
        const isPlaying = gameEnded == false;
        module.setErrorData({
          wsCloseIsPlaying: isPlaying,
          wsCloseTime: now,
          wsCloseGameId: gameId,
        });

        if (isPlaying && gameId && shouldReportLiveClose(gameId, e.code, now)) {
          module.sendWebsocketCloseError({
            code: e.code,
            reason: e.reason,
            wasClean: e.wasClean,
            readyState: closeReadyState,
            url: closeUrl,
            gameId,
            isPlaying,
            timestamp: now,
            sessionId: getOrCreateSessionId(),
          });
        }

        if (isPlaying && gameId && e.code === 1006) {
          const closeCount = noteLiveGame1006Close(gameId, now);
          if (
            closeCount >= AUTO_LONG_POLLING_MIN_1006_CLOSES &&
            startAutoLongPollingFallback("repeated_1006_live_game_close", {
              gameId,
              closeCount,
              firstCloseAt: firstLiveGame1006CloseAt,
              closeWindowMs: now - firstLiveGame1006CloseAt,
              closeEvent: {
                code: e.code,
                reason: e.reason,
                wasClean: e.wasClean,
                readyState: closeReadyState,
                url: closeUrl,
              },
              ping: {
                retries: pingState.retries,
                timeSinceLastPong:
                  now -
                  (pingState.errorData.eventSequence.lastPongReceived || 0),
                timeSinceLastVisibilityChange:
                  now -
                  (pingState.errorData.connectionState.lastVisibilityChange ||
                    0),
              },
              watchdog: getCompactWatchdogState(
                getWatchdogState("websocket-close", now)
              ),
            })
          ) {
            return;
          }
        }

        setTimeout(() => {
          module.setRetries();
          retries++;
          startReconnectTimer();
          initWebsocketConnect();
        }, 1000);
      });
    });
  };
  websocket.onopen = () => {
    console.log("websocket open");
    wsDebug("websocket open", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_open", {
      readyState: websocket?.readyState,
      url: websocket?.url,
      retries,
    });

    setWsConnected(true);

    endReconnectTimer();
    websocketOpenedAt = Date.now();
    lastPongAt = websocketOpenedAt;
    lastMessageAt = websocketOpenedAt;
    recordWatchdogState("websocket-open", websocketOpenedAt);
    startPing();
    websocket.sendPing();

    import("../redux/lobby").then((module) => {
      module.clearInvites();
      module.enterLobby();
    });

    loginServer();
    setupOfflineDetection();
  };

  websocket.onmessage = async (e) => {
    const messageAt = Date.now();
    lastMessageAt = messageAt;

    if (e.data === "pong") {
      lastPongAt = messageAt;
      reportRussiaWebsocketEvent("websocket_pong", {
        latencyMs: lastPingAt ? lastPongAt - lastPingAt : null,
        url: websocket?.url,
      });
      setPingErrorData({
        eventSequence: {
          lastPongReceived: lastPongAt,
        },
        websocketWatchdog: getWatchdogState("pong", lastPongAt),
      });
      clearTimeout(postTimeout);
      return;
    }

    let data: any;
    try {
      data = JSON.parse(e.data);
    } catch (e) {
      console.log("JSON parse error", e);
      wsDebug("websocket JSON parse error", {
        raw: typeof e?.toString === "function" ? e.toString() : "unknown",
      });
      return;
    }
    if (data?.cmd === "COMP" || data?.cmd === "ONLI" || data?.cmd === "WS") {
      wsDebug("websocket message", data);
    }
    routeIncommingMessage(data);

    if (data?.cmd === "COMP" && shouldUseLongPollingTransport()) {
      void startLongPollingTransport("manual_param_login_complete", {
        automatic: false,
      });
    }
  };

  websocket.sendPing = () => {
    try {
      lastPingAt = Date.now();
      websocket.send("ping");
      setPingErrorData({
        eventSequence: {
          lastPingSent: lastPingAt,
        },
        websocketWatchdog: getWatchdogState("ping", lastPingAt),
      });
    } catch (e) {
      setTimeout(() => {
        websocket.sendPing();
      }, 1000);
    }
  };
}

function startPing() {
  clearInterval(pingInterval);

  pingInterval = setInterval(() => {
    try {
      if (websocket && websocket.readyState == WebSocket.OPEN) {
        websocket.sendPing();
      }
    } catch (e) {
      console.log(e);
    }
  }, PING_INTERVAL_MS);
}

// async function longPollingConnect() {
//   console.log("switchToLongPolling");
//   disconnectWebSocket();

//   useLongPolling = true;
//   const username = userStore.getState().username;
//   const sessionId = sessionStorage.getItem("sessionId");

//   const { gamePlayStore } = await import("../redux/gamePlay");
//   const { gameId } = gamePlayStore.getState();

//   await longPollingGetRequest(username, sessionId, gameId);
//   setWsConnected(true);
// }
